/*
 * Overlap resolution for the unified Calendar component.
 * DESIGN_SYSTEM.md §2.2 — events MUST NEVER visually overlap; events sharing
 * any time range split horizontally into N equal-width lanes.
 *
 * Algorithm (greedy interval coloring):
 *   1. Sort events in the column by start time, ties broken by end time.
 *   2. Walk events; for each, pick the lowest free lane (a lane is free if
 *      its current "tail" — the largest end time of events assigned to it —
 *      is ≤ the new event's start).
 *   3. Cluster boundary: when an event has lane=0 AND no existing lane's tail
 *      extends past its start, a new cluster begins. All events that share
 *      time with anything in the cluster get `lanes` = max(lane index)+1.
 *   4. If a cluster contains > MAX_VISIBLE_LANES events at any point, only
 *      the first MAX_VISIBLE_LANES-1 lanes render visibly; lane index ==
 *      MAX_VISIBLE_LANES-1 carries `overflow: number` for the "+N more"
 *      affordance.
 *
 * Touching boundaries (e1.end === e2.start) DO NOT overlap — back-to-back
 * appointments share zero time.
 */

export interface Interval {
  start: number; // ms epoch
  end: number;   // ms epoch
}

export interface LaidOutEvent<T extends Interval> {
  event: T;
  /** 0-indexed lane within the cluster. */
  lane: number;
  /** Total lanes in the cluster this event belongs to. */
  lanes: number;
  /** When > 0, this slot is the "+N more" overflow indicator. */
  overflow?: number;
  /** True when this event was hidden by overflow collapsing. */
  hidden?: boolean;
}

export const MAX_VISIBLE_LANES = 4;

/**
 * Layout events into lanes within a single host column.
 * Pure function — no DOM, no time-zone math, no Date deps. Caller passes
 * milliseconds (Date.getTime()).
 *
 * Time complexity: O(n log n) for the sort, O(n × lanes) for assignment.
 * Practical n is small (a day's events) so this is trivial.
 */
export function layoutEvents<T extends Interval>(events: T[]): LaidOutEvent<T>[] {
  if (events.length === 0) return [];

  const sorted = [...events].sort((a, b) =>
    a.start === b.start ? a.end - b.end : a.start - b.start,
  );

  // Each lane holds the largest end-time of events placed in it ("tail").
  // Reset between clusters.
  let laneTails: number[] = [];
  // Track the cluster: indices into the result array, plus the max lane seen.
  let clusterStart = 0;
  let clusterMaxLane = 0;
  // Track the cluster end (max end time seen so far in cluster).
  let clusterEnd = -Infinity;

  const result: LaidOutEvent<T>[] = [];

  const closeCluster = (untilIdx: number) => {
    const lanes = clusterMaxLane + 1;
    for (let i = clusterStart; i < untilIdx; i++) {
      result[i].lanes = lanes;
    }
  };

  for (let i = 0; i < sorted.length; i++) {
    const ev = sorted[i];

    // New cluster?
    if (ev.start >= clusterEnd) {
      closeCluster(i);
      clusterStart = i;
      clusterMaxLane = 0;
      clusterEnd = ev.end;
      laneTails = [ev.end];
      result.push({ event: ev, lane: 0, lanes: 1 });
      continue;
    }

    // Same cluster — find lowest free lane.
    let lane = laneTails.findIndex((tail) => tail <= ev.start);
    if (lane === -1) {
      lane = laneTails.length;
      laneTails.push(ev.end);
    } else {
      laneTails[lane] = ev.end;
    }

    if (lane > clusterMaxLane) clusterMaxLane = lane;
    if (ev.end > clusterEnd) clusterEnd = ev.end;
    result.push({ event: ev, lane, lanes: 1 /* will fix on close */ });
  }
  closeCluster(sorted.length);

  // Apply overflow collapsing per cluster.
  return collapseOverflow(result);
}

function collapseOverflow<T extends Interval>(
  laid: LaidOutEvent<T>[],
): LaidOutEvent<T>[] {
  if (laid.length === 0) return laid;

  // Group by cluster (events with identical `lanes` value AND adjacency).
  // Easier: walk and cut on lane===0 + new cluster start; we already know
  // clusters from the first pass — re-derive by finding contiguous runs that
  // share a `lanes` value AND start with lane=0.
  const out: LaidOutEvent<T>[] = [];
  let i = 0;
  while (i < laid.length) {
    // Cluster runs while events overlap. Easiest re-detect: continue while
    // the next event's start is < the running cluster end.
    const clusterStartIdx = i;
    let clusterEnd = laid[i].event.end;
    let j = i + 1;
    while (j < laid.length && laid[j].event.start < clusterEnd) {
      if (laid[j].event.end > clusterEnd) clusterEnd = laid[j].event.end;
      j++;
    }

    const clusterItems = laid.slice(clusterStartIdx, j);
    const lanes = clusterItems.reduce((m, x) => Math.max(m, x.lane), 0) + 1;

    if (lanes <= MAX_VISIBLE_LANES) {
      for (const it of clusterItems) {
        out.push({ ...it, lanes });
      }
    } else {
      // First MAX_VISIBLE_LANES - 1 visible; rest hidden; emit one overflow
      // marker on the last visible lane.
      const visibleLanesCount = MAX_VISIBLE_LANES;
      const hiddenCount = clusterItems.filter(
        (it) => it.lane >= visibleLanesCount - 1,
      ).length - 1; // -1 because one item still occupies the overflow lane.

      let overflowEmitted = false;
      for (const it of clusterItems) {
        if (it.lane < visibleLanesCount - 1) {
          out.push({ ...it, lanes: visibleLanesCount });
        } else if (!overflowEmitted) {
          out.push({
            ...it,
            lane: visibleLanesCount - 1,
            lanes: visibleLanesCount,
            overflow: hiddenCount,
          });
          overflowEmitted = true;
        } else {
          out.push({ ...it, lane: visibleLanesCount - 1, lanes: visibleLanesCount, hidden: true });
        }
      }
    }

    i = j;
  }
  return out;
}
